import { Link } from "react-router-dom";

import { Pill } from "../components/Badges";
import { DataTable } from "../components/DataTable";
import { useFeedback } from "../components/Feedback";
import { IssueList } from "../components/IssueList";
import { Card, Empty, ErrorBox, Loading, PageHeader } from "../components/Page";
import { api, type Issue } from "../lib/api";
import { day, num, when } from "../lib/format";
import { useApi } from "../lib/useApi";

interface Prod {
  as_of: string | null; run_id: number; fetched_at: string;
  runs: { run_id: number; date: string | null; line: string; product: string; cases: string; packaging: string; spirits: string }[];
  materials: { id: string; item: string; uom: string; total: string; by_month: Record<string, string>; on_hand: number; gap: number }[];
  issues: Issue[];
}

export function Production() {
  const { data, error, reload } = useApi<Prod>("/production");
  const { toast } = useFeedback();
  const sync = async () => {
    try {
      const r = await api.post<{ results: { skipped: boolean; rows: number }[] }>("/sources/BCI_SHEET/sync");
      toast(r.results[0]?.skipped ? "BCI sheet unchanged since the last sync" : `BCI sheet synced: ${r.results[0]?.rows} rows`);
      reload();
    } catch (e) { toast((e as Error).message, "error"); }
  };
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  return (
    <>
      <PageHeader title="Production" sub={<>BCI, Morrison TN — from their live dashboard. Last read {when(data.fetched_at)} (sheet dated {day(data.as_of)}).</>}
        actions={<><button className="btn btn-primary" onClick={sync}>Sync BCI now</button>
          <Link className="btn" to={`/imports/${data.run_id}`}>View raw import</Link></>} />
      <Card title="Scheduled runs">
        {data.runs.length === 0 ? <Empty>No runs scheduled.</Empty> : (
          <DataTable rows={data.runs} rowKey={(r) => r.run_id} filterPlaceholder={false} testId="bci-runs" columns={[
            { key: "run_id", label: "Run", render: (r) => `#${r.run_id}` },
            { key: "date", label: "Date", render: (r) => day(r.date) },
            { key: "product", label: "Product" }, { key: "line", label: "Line" },
            { key: "cases", label: "Cases", numeric: true, value: (r) => Number(r.cases), render: (r) => num(Number(r.cases)) },
            { key: "packaging", label: "Packaging", render: (r) => <Status flag={r.packaging} /> },
            { key: "spirits", label: "Spirits", render: (r) => <Status flag={r.spirits} /> },
          ]} />
        )}
      </Card>
      <Card title="Materials BCI still has to source">
        <DataTable rows={data.materials} rowKey={(m) => m.id} filterPlaceholder={false} testId="bci-materials"
          initialSort={{ key: "gap", dir: "asc" }} columns={[
            { key: "item", label: "Material" }, { key: "id", label: "BCI code", render: (m) => <code>{m.id}</code> },
            { key: "on_hand", label: "On hand", numeric: true, render: (m) => `${num(m.on_hand)} ${m.uom}` },
            { key: "gap", label: "Still to source", numeric: true,
              render: (m) => (m.gap < 0 ? <span className="bad strong">{num(-m.gap)} {m.uom}</span> : <Pill tone="good">covered</Pill>) },
          ]} />
        <p className="note muted">BCI's own figure, after what they already hold, including 3% for production loss.</p>
      </Card>
      <Card title="What this means"><IssueList issues={data.issues} compact /></Card>
    </>
  );
}

function Status({ flag }: { flag: string }) {
  if (!flag) return <span className="muted">—</span>;
  const short = flag.includes("✗");
  const n = flag.replace(/\D/g, "");
  return short ? <Pill tone="error">✕ short{n ? ` (${n})` : ""}</Pill> : <Pill tone="good">✓ ready</Pill>;
}
